import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Space,
  Tooltip,
  Upload,
} from 'antd';

import {
  SettingOutlined,
  UploadOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import FormWrapper from '../FormWrapper';
import DeleteItemIcon from '../buttons/DeleteItemIcon';
import useDictionaries from '../../hooks/useDictionaries';
import './OrderFileList.scss';
import {
  getAvailableProducts,
  getProducts,
} from '../../services/offers/products';
import { FormContext } from '../../hooks/useForm';
import ProductUsersList from './ProductUsersList';
import { csvToJson } from '../../helpers/csv';
import { getUsersWithExistingCards } from '../../services/admin/users';
import { getRolesWithDetails } from '../../services/admin/roles';
import { getUserTypes } from '../../services/taxonomies/user-types';
import POSButton from '../pos2/POSButton';
import Div from '../pos2/Div';
import Restriction from './Restriction';
import { BULK_TYPES, MIFARE_TYPES } from '../../pages/orders/constants';
import { getSubdivisions } from '../../services/taxonomies/subdivisions';
import SearchInstitutions from './SearchInstituions';
import DatePickerLocale from '../DatePickerLocale';
import ZonalRestriction from './ZonalRestriction';
import { getAxisWithAdminUnits } from '../../services/taxonomies/zonal-restriction';

const dictionaries = {
  productTypes: getAvailableProducts,
  roles: getRolesWithDetails,
  userTypes: getUserTypes,
};

const Products = ({ status, permisions }) => {
  const form = useContext(FormContext);
  const [configForm] = Form.useForm();
  const { t } = useTranslation();
  const [cardProduct, setCardProduct] = useState(null);
  const [categoryProduct, setCategoryProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [axis, setAxis] = useState([]);
  const [uats, setUats] = useState([]);

  const metropolitanEnabled = useMemo(
    () => window._env_.METROPOLITAN_ENABLE === 'true',
    [],
  );

  const { select, custom, value, setValue, touch, input } = form;
  const { products = [], disabled, bulkType, id: orderId } = value;

  const extractUniqueAdminUnits = (axisData) => {
    const allUnits = axisData.flatMap((a) => a.administrativeUnits ?? []);
    const uniqueUnitsMap = new Map(allUnits.map((unit) => [unit.id, unit]));
    return Array.from(uniqueUnitsMap.values());
  };

  const nenomialPassPersonMaxCount = useMemo(() => {
    const val = Number(window._env_.NENOMINAL_PASS_PERSONS_MAX_COUNT);
    return Number.isNaN(val) ? 10 : val; // default to 10 if invalid
  }, []);

  useEffect(() => {
    if (metropolitanEnabled) {
      getAxisWithAdminUnits().then((response) => {
        setAxis(response);
        const adminUnits = extractUniqueAdminUnits(response);
        setUats(adminUnits);
      });
    }
  }, [metropolitanEnabled]);

  const notStorableProducts =
    products && products.length > 1
      ? products.filter(
          (product) =>
            product.productCode !== MIFARE_TYPES.MIFARE_1K &&
            product.productCode !== MIFARE_TYPES.MIFARE_1K_NENOMINAL,
        )
      : products;
  const showCardProduct = !(
    products.length === 1 && products.find((p) => p.productId === 114)
  ); // fixme wtf is this 114???
  let requiredFields;
  let omittedFields;

  switch (bulkType) {
    case BULK_TYPES.ISSUING_CARDS:
      requiredFields = ['fullname', 'identityCard'];
      omittedFields = [
        'email',
        'fullname',
        'identityCard',
        'employeeNumber',
        'metaData1',
        'metaData2',
        'phone',
        'subdivisionId',
      ];
      break;
    case BULK_TYPES.RECHARGE_CARDS:
      requiredFields = ['cardLogicalId'];
      omittedFields = [
        'email',
        'cardLogicalId',
        'fullname',
        'metaData1',
        'metaData2',
        'subdivisionId',
      ];
      break;
    case BULK_TYPES.ISSUING_NON_NOMINAL_CARDS:
      requiredFields = ['fullname'];
      omittedFields = [
        'email',
        'fullname',
        'identityCard',
        'employeeNumber',
        'metaData1',
        'metaData2',
        'phone',
        'subdivisionId',
      ];
      break;
    default:
      requiredFields = null;
  }

  if (!products[0]) {
    products.push({ productPersons: [] });
  }

  const [{ productTypes, roles, userTypes }] = useDictionaries(dictionaries);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const sortedRoles = roles?.content
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  dayjs.extend(customParseFormat);

  useEffect(() => {
    if (!cardProduct) {
      getProducts({
        criterias: {
          code:
            bulkType === BULK_TYPES.ISSUING_NON_NOMINAL_CARDS
              ? MIFARE_TYPES.MIFARE_1K_NENOMINAL
              : MIFARE_TYPES.MIFARE_1K,
          dateEnd: moment().toISOString(),
        },
      }).then((data) => {
        setCardProduct(data.content[0]);
      });
    }
  }, [cardProduct, products, setCardProduct, bulkType]);

  // eslint-disable-next-line arrow-body-style
  const disabledDate = (current) => {
    // Can not select days before today and today
    return (
      current < moment() ||
      Date.parse(current) > Date.parse(moment().add(90, 'days'))
    );
  };

  const configureProduct = useCallback(
    (product, idx) => {
      const originalProduct = productTypes?.content.find(
        ({ id }) => id === product?.productId,
      );
      setCategoryProduct(originalProduct.category);
      setSelectedProduct(originalProduct);

      configForm.setFieldsValue({
        product: {
          ...product,
          count:
            (originalProduct.category === 'PURSE'
              ? product.count / 100
              : product.count) || 1,
          startDate: product.startDate && moment(product.startDate),
        },
        idx,
        originalProduct,
      });
      setIsModalVisible(true);
    },
    [configForm, productTypes],
  );

  const onRemove = useCallback(
    async (record) =>
      setValue(
        'products',
        products.filter((product) =>
          record.key ? product.key !== record.key : product.id !== record.id,
        ),
      ),
    [products, setValue],
  );

  const getProductFields = (type) => {
    switch (type) {
      case BULK_TYPES.ISSUING_CARDS:
        return [
          'email',
          'fullname',
          'foreignCitizen',
          'identityCard',
          'phone',
          'employeeNumber',
          'metaData1',
          'subdivisionId',
        ];
      case BULK_TYPES.ISSUING_NON_NOMINAL_CARDS:
        return ['fullname'];
      case BULK_TYPES.RECHARGE_CARDS:
      default:
        return [
          'cardLogicalId',
          'email',
          'fullname',
          'metaData1',
          'subdivisionId',
        ];
    }
  };

  const getSubdivisionsByInstitutionId = useCallback((institutionId) => {
    if (institutionId) {
      return getSubdivisions({
        pageSize: 1000,
        criterias: { institutionId },
      }).then((res) => {
        return Promise.resolve(res.content);
      });
    }
    return [];
  }, []);

  const getUserTypeId = useCallback(
    (productId) => {
      return productTypes?.content?.find((pt) => pt.id === productId)
        ?.userTypeId;
    },
    [productTypes.content],
  );

  const getProductCode = useCallback(
    (productId) => {
      return productTypes?.content?.find((pt) => pt.id === productId)?.code;
    },
    [productTypes.content],
  );

  const getProductCategory = useCallback(
    (productId) => {
      return productTypes?.content?.find((pt) => pt.id === productId)?.category;
    },
    [productTypes.content],
  );

  const getCSVData = useCallback(
    async (idx, file, expectedFields, excludedFields) => {
      const createErrorMessage = (validationResults) => {
        const { missingFields, extraFields } = validationResults;
        const missingFieldsMsg =
          missingFields.length > 0 && missingFields.every(Boolean)
            ? `${t('errors.missingFields')}: ${missingFields.join(', ')}`
            : '';
        const extraFieldsMsg =
          extraFields.length > 0 && extraFields.every(Boolean)
            ? `${missingFieldsMsg ? `${t('conjunctions.and')} ` : ''}${
                missingFieldsMsg
                  ? t('errors.extraFields').toLowerCase()
                  : t('errors.extraFields')
              }: ${extraFields.join(', ')}`
            : '';

        return `${missingFieldsMsg} ${extraFieldsMsg}`;
      };

      const validateCSVFields = (csvData) => {
        const actualFields = csvData.length > 0 ? Object.keys(csvData[0]) : [];
        const missingFields = expectedFields.filter(
          (field) => !actualFields.includes(field),
        );

        const extraFields = actualFields.filter(
          (field) => !excludedFields.includes(field),
        );

        return { missingFields, extraFields };
      };

      // Simple verification that the file is a CSV
      if (file.type !== 'text/csv') {
        notification.error({
          message: t('errors.invalidFileType'),
        });
        return Promise.resolve(null);
      }

      const { productPersons = [], institutionId } = products[idx];
      const userTypeId = getUserTypeId(products[idx].productId);
      const subdivisions = await getSubdivisionsByInstitutionId(institutionId);

      const reader = new FileReader();
      reader.onload = (e) => {
        const csvData = csvToJson(e.target.result);

        csvData.forEach((pers, index, array) => {
          const matchingSubdivision = subdivisions.find(
            (s) => s.name === pers.subdivisionId,
          );
          if (matchingSubdivision) {
            // eslint-disable-next-line no-param-reassign
            array[index] = { ...pers, subdivisionId: matchingSubdivision.id };
          }
        });

        const validationResults = validateCSVFields(csvData);

        if (!validationResults) {
          notification.error({
            message: t('errors.emptyFile'),
          });
          return;
        }

        const { missingFields, extraFields } = validationResults;
        if (missingFields.length > 0 || extraFields.length > 0) {
          notification.error({
            message: t('errors.csv'),
            description: createErrorMessage(validationResults),
          });
          return;
        }

        const mailList = csvData.map((pers) => pers.email);
        getUsersWithExistingCards(mailList).then((emails) => {
          const validEmails = emails.filter(
            (mail) => mail !== null && mail !== '',
          );

          if (validEmails.length > 0) {
            notification.warning({
              message: t('errors.existingCardsForEmails', {
                emails: validEmails.join(', '),
              }),
            });
          }
        });

        const validSubinstNames = (subdivisions || []).map((sdv) => sdv.name);
        const usersSubinsts =
          csvData.map((pers) => pers.subinstitution).filter(Boolean) || [];
        const invalidSubinst =
          usersSubinsts.filter(
            (siName) => !validSubinstNames.includes(siName),
          ) || [];

        if (invalidSubinst.length > 0) {
          notification.warning({
            message: t('errors.invalidSubinstitutions', {
              list: invalidSubinst.join(', '),
            }),
          });
          return;
        }

        setValue(`products[${idx}].productPersons`, [
          ...productPersons,
          ...csvData.map((p) => ({
            person: {
              ...p,
              email: p.email === '' ? null : p.email,
              foreignCitizen: p.foreignCitizen === 'TRUE',
              userTypeId,
              key: Math.random(),
            },
            key: Math.random(),
          })),
        ]);
      };

      reader.readAsText(file);
      return Upload.LIST_IGNORE;
    },
    [t, products, setValue, getSubdivisionsByInstitutionId, getUserTypeId],
  );

  const addUser = useCallback(
    (index) => {
      const { productPersons = [] } = products[index];
      const userTypeId = getUserTypeId(products[index].productId);
      setValue(`products[${index}].productPersons`, [
        ...productPersons,
        {
          person: {
            key: Math.random(),
            userTypeId,
          },
          key: Math.random(),
        },
      ]);
    },
    [setValue, products, getUserTypeId],
  );

  const addNonNominalUser = useCallback(
    (index, val) => {
      const count = parseInt(val, 10) || 0;
      const userTypeId = getUserTypeId(products[index].productId);

      const newPersons = Array.from({ length: count }, () => ({
        person: {
          key: Math.random(),
          userTypeId,
          fullname: ` `,
        },
        key: Math.random(),
      }));

      setValue(`products[${index}].productPersons`, newPersons);
    },
    [setValue, products, getUserTypeId],
  );

  return (
    <>
      {/* <PageTitle title={t('entity.orders.productList._plural')}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={disabled}
          onClick={() => {
            setValue('products', [...products, { key: Math.random() }]);
          }}
        >
          {t('entity.orders.add')}
        </Button>
        </PageTitle> */}
      {notStorableProducts.map((product, idx) => (
        <>
          <FormWrapper key={product.key || product.id}>
            <FormWrapper.Single>
              <FormWrapper.StaticSearch
                label={t('entity.orders.product')}
                props={{
                  disabled,
                  options: productTypes.content
                    .map((type) => ({
                      ...type,
                      disabled: products.find((p) => p.productId === type.id),
                    }))
                    .filter((pr) => {
                      if (bulkType === BULK_TYPES.RECHARGE_CARDS) {
                        return pr.category !== 'OPERATOR';
                      }
                      return true;
                    }),
                  ...select(`products[${idx}].productId`),
                  onChange: (productId) => {
                    const userTypeId = getUserTypeId(productId);
                    const productCode = getProductCode(productId);
                    const productCategory = getProductCategory(productId);
                    const productPersons = value?.products[
                      idx
                    ]?.productPersons?.map((pp) => ({
                      ...pp,
                      person: {
                        ...pp.person,
                        subdivisionId: null,
                        userTypeId,
                      },
                    }));
                    const originalProduct = productTypes?.content.find(
                      ({ id }) => id === productId,
                    );
                    const lgRestriction =
                      originalProduct?.productRestrictions?.find(
                        (r) => r.restriction === 'LINE_GROUPS',
                      );
                    let lineGroups = null;
                    if (lgRestriction) {
                      const {
                        value1,
                        value2,
                        value3,
                        value4,
                        value5,
                        value6,
                        value7,
                        value8,
                      } = lgRestriction;
                      const opts = {};
                      if (value1 !== 1 && value2) opts.option1 = value2;
                      if (value3 !== 1 && value4) opts.option2 = value4;
                      if (value5 !== 1 && value6) opts.option3 = value6;
                      if (value7 !== 1 && value8) opts.option4 = value8;
                      if (Object.keys(opts).length > 0) lineGroups = opts;
                    }
                    setValue(`products[${idx}]`, {
                      ...products[idx],
                      productId,
                      productPersons,
                      productCode,
                      productCategory,
                      count: null,
                      institutionId: null,
                      startDate: null,
                      lines: null,
                      lineGroups,
                      zonal: null,
                    });
                    setValue(`productStartDate`, null);
                    setValue('isConfigured', false);

                    touch(`products[${idx}].productId`);
                  },
                }}
              />
              {Array.isArray(products) &&
                products[idx] &&
                typeof products[idx].lines === 'object' &&
                products[idx].lines !== null &&
                Object.values(products[idx].lines).some(
                  (v) => v !== null && v !== undefined,
                ) && (
                  <div>
                    {t('entity.account.transportTitle.lines')}:{' '}
                    {['option1', 'option2', 'option3', 'option4']
                      .map((key) => products[idx].lines[key])
                      .filter((v) => v !== null && v !== undefined)
                      .join(', ')}
                  </div>
                )}
            </FormWrapper.Single>
            {bulkType === BULK_TYPES.ISSUING_NON_NOMINAL_CARDS && (
              <FormWrapper.Single>
                <Space.Compact>
                  <FormWrapper.Number
                    label={t('entity.orders.cardsCount')}
                    props={{
                      ...input('cardCount'),
                      min: '0',
                      step: '1',
                      max: nenomialPassPersonMaxCount,
                      // size: 'small',
                      disabled: orderId || disabled,
                      onChange: (val) => {
                        setValue('cardCount', val);
                      },
                      onKeyDown: (e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // stop Enter key
                        }
                      },
                    }}
                  />
                  <Button
                    type="primary"
                    disabled={disabled}
                    onClick={() => {
                      addNonNominalUser(idx, value?.cardCount);
                    }}
                    style={{ marginTop: 22 }}
                  >
                    {t('entity.orders.addNenominalPersonsToOrder')}
                  </Button>
                </Space.Compact>
              </FormWrapper.Single>
            )}
            {[
              BULK_TYPES.ISSUING_CARDS,
              BULK_TYPES.ISSUING_NON_NOMINAL_CARDS,
              BULK_TYPES.RECHARGE_CARDS,
            ].includes(bulkType) && (
              <Col span={12}>
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
                  {[
                    BULK_TYPES.ISSUING_CARDS,
                    BULK_TYPES.ISSUING_NON_NOMINAL_CARDS,
                  ].includes(bulkType) ? (
                    <FormWrapper.Single span={8}>
                      {showCardProduct && cardProduct ? (
                        <FormWrapper.Input
                          label={t('entity.orders.card')}
                          props={{
                            disabled: true,
                            value: cardProduct.name,
                          }}
                        />
                      ) : null}
                    </FormWrapper.Single>
                  ) : null}
                  <Col
                    span={
                      [
                        BULK_TYPES.ISSUING_CARDS,
                        BULK_TYPES.ISSUING_NON_NOMINAL_CARDS,
                      ].includes(bulkType)
                        ? 8
                        : 12
                    }
                  >
                    <FormWrapper.Select
                      label={t('entity.orders.role')}
                      props={{
                        disabled,
                        showSearch: true,
                        allowClear: true,
                        options: sortedRoles,
                        ...select(`products[${idx}].roleId`),
                        filterOption: (input2, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input2.toLowerCase()) !== -1,
                      }}
                    />
                  </Col>
                  <Col
                    span={
                      [
                        BULK_TYPES.ISSUING_CARDS,
                        BULK_TYPES.ISSUING_NON_NOMINAL_CARDS,
                      ].includes(bulkType)
                        ? 8
                        : 12
                    }
                  >
                    <SearchInstitutions
                      label={t('entity.admin.user.institutionId')}
                      props={{
                        disabled,
                        searchField: 'name',
                        field: 'id',
                        resetSubdivisions: () =>
                          setValue(`products[${idx}]`, {
                            ...products[idx],
                            productPersons: products[idx].productPersons?.map(
                              (pers) => ({
                                ...pers,
                                subdivisionId: null,
                              }),
                            ),
                          }),
                        userTypes,
                        userTypeId: getUserTypeId(products[idx].productId),
                        ...custom(`products[${idx}].institutionId`),
                      }}
                    />
                  </Col>
                  {orderId && metropolitanEnabled && (
                    <>
                      <Col
                        span={bulkType === BULK_TYPES.ISSUING_CARDS ? 8 : 12}
                      >
                        <FormWrapper.Select
                          label={t(
                            'entity.offers.restrictions.restriction.ZONAL.uatStart',
                          )}
                          props={{
                            disabled: true,
                            options: uats,
                            ...select(`products[${idx}].zonal.option1`),
                          }}
                        />
                      </Col>
                      <Col
                        span={bulkType === BULK_TYPES.ISSUING_CARDS ? 8 : 12}
                      >
                        <FormWrapper.Select
                          label={t(
                            'entity.offers.restrictions.restriction.ZONAL.uatStop',
                          )}
                          props={{
                            disabled: true,
                            options: uats,
                            ...select(`products[${idx}].zonal.option2`),
                          }}
                        />
                      </Col>
                    </>
                  )}
                </Row>
              </Col>
            )}
            <Col span={1}>
              <div className="ant-typography" />
              <Tooltip title={t('entity.orders.configureProduct')}>
                <Button
                  disabled={disabled}
                  shape="circle"
                  type="link"
                  icon={<SettingOutlined style={{ fontSize: '150%' }} />}
                  onClick={() => configureProduct(product, idx)}
                />
              </Tooltip>
            </Col>
            {[
              BULK_TYPES.ISSUING_CARDS,
              BULK_TYPES.RECHARGE_CARDS,
              BULK_TYPES.DISABLE_CARDS,
            ].includes(bulkType) && (
              <Col span={1}>
                <div className="ant-typography" />
                <Tooltip title={t('entity.orders.uploadPersons')}>
                  <Upload
                    size="small"
                    dragger={false}
                    beforeUpload={(file) =>
                      getCSVData(idx, file, requiredFields, omittedFields)
                    }
                    multiple={false}
                    accept=".csv"
                    disabled={disabled || !products[idx].productId}
                  >
                    <Button
                      icon={
                        <UploadOutlined
                          style={{ fontSize: '150%', color: '#08c' }}
                        />
                      }
                    />
                  </Upload>
                </Tooltip>
              </Col>
            )}
            {[
              BULK_TYPES.ISSUING_CARDS,
              BULK_TYPES.RECHARGE_CARDS,
              BULK_TYPES.DISABLE_CARDS,
            ].includes(bulkType) && (
              <Col span={1}>
                <div className="ant-typography" />
                <Tooltip title={t('entity.orders.addPerson')}>
                  <Button
                    disabled={disabled}
                    shape="circle"
                    type="link"
                    icon={<UsergroupAddOutlined style={{ fontSize: '150%' }} />}
                    onClick={() => addUser(idx)}
                  />
                </Tooltip>
              </Col>
            )}
            <Col span={1}>
              <div className="ant-typography" />
              <DeleteItemIcon
                title={t('entity.orders.productList._delete', product.id)}
                message={t('entity.orders.productList._deleted', product.id)}
                item={product}
                disabled={disabled}
                action={onRemove}
                reload={() => null}
                size="large"
              />
            </Col>
          </FormWrapper>
          <Row>
            <Col span={24}>
              {product.productPersons && product.productPersons.length > 0 && (
                <ProductUsersList
                  index={idx}
                  disabled={disabled}
                  institutionId={products[idx].institutionId}
                  status={status}
                  bulkType={bulkType}
                  permisions={permisions}
                  fields={getProductFields(bulkType)}
                />
              )}
            </Col>
          </Row>
          <Space direction="vertical">
            <Divider dashed />
          </Space>
        </>
      ))}

      <Modal
        title={t('entity.orders.configuration')}
        visible={isModalVisible}
        onOk={() => {
          configForm
            .validateFields()
            .then((val) => {
              const idx = configForm.getFieldValue('idx');
              const { product } = val;

              setValue('productStartDate', product.startDate);

              if (categoryProduct === 'PURSE') {
                if (
                  selectedProduct?.maxValue &&
                  product.count * 100 > selectedProduct.maxValue
                ) {
                  return;
                }
                product.count *= 100;
              }

              setValue(`products[${idx}]`, {
                ...products[idx],
                ...product,
              });
              setValue('isConfigured', true);
              setIsModalVisible(false);
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
        onCancel={() => {
          configForm.resetFields();
          setIsModalVisible(false);
        }}
      >
        <Form form={configForm} layout="vertical">
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              prev.originalProduct?.category !== next.originalProduct?.category
            }
          >
            {({ getFieldValue }) => {
              const categ = getFieldValue(['originalProduct', 'category']);
              return (
                <>
                  <Form.Item name={['originalProduct', 'name']} noStyle>
                    <Div />
                  </Form.Item>
                  {categ && (categ === 'TICKET' || categ === 'PURSE') && (
                    <Row gutter={16}>
                      <Col span={8}>
                        <POSButton
                          title={t('entity.orders.subtract')}
                          onClick={() =>
                            configForm.setFieldsValue({
                              product: {
                                ...configForm.getFieldValue('product'),
                                count: Math.max(
                                  1,
                                  configForm.getFieldValue([
                                    'product',
                                    'count',
                                  ]) - 1,
                                ),
                              },
                            })
                          }
                          color="red"
                          size="xsmall"
                        />
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          noStyle
                          name={['product', 'count']}
                          rules={[
                            {
                              type: 'number',
                              message: t('errors.maxValueExceeded'),
                            },
                          ]}
                        >
                          {categ === 'PURSE' ? (
                            <InputNumber
                              className="account-custom-input-number"
                              min={1}
                              max={
                                getFieldValue(['originalProduct', 'maxValue'])
                                  ? getFieldValue([
                                      'originalProduct',
                                      'maxValue',
                                    ]) / 100
                                  : undefined
                              } // Conditionally add the upper bound
                              defaultValue={1}
                              step={0.01}
                              addonAfter="RON"
                              parser={(purseVal) => purseVal.replace(',', '.')}
                            />
                          ) : (
                            <Input
                              style={{ height: '100%', fontSize: '2rem' }}
                              disabled
                            />
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <POSButton
                          title={t('entity.orders.add')}
                          onClick={() =>
                            configForm.setFieldsValue({
                              product: {
                                ...configForm.getFieldValue('product'),
                                count:
                                  configForm.getFieldValue([
                                    'product',
                                    'count',
                                  ]) + 1,
                              },
                            })
                          }
                          color="green"
                          size="xsmall"
                        />
                      </Col>
                    </Row>
                  )}
                </>
              );
            }}
          </Form.Item>

          <Divider dashed />

          <Form.Item
            noStyle
            shouldUpdate={(prev, next) => {
              return prev?.product?.productId !== next?.product?.productId;
            }}
          >
            {({ getFieldValue }) => {
              return (
                getFieldValue(['originalProduct', 'validityStartType']) ===
                  'FUTURE' && (
                  <Form.Item
                    name={['product', 'startDate']}
                    label={t('entity.orders.startDateSubscription')}
                    rules={[
                      {
                        required: true,
                        message: t('errors.selectDate'),
                      },
                    ]}
                  >
                    <DatePickerLocale
                      format="DD.MM.YYYY HH:mm"
                      showTime={{ defaultValue: dayjs('00:00:00', 'HH:mm') }}
                      style={{ width: '100%' }}
                      disabledDate={disabledDate}
                    />
                  </Form.Item>
                )
              );
            }}
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, next) => {
              return prev?.originalProduct?.id !== next?.originalProduct?.id;
            }}
          >
            {({ getFieldValue }) => {
              const restrictions = getFieldValue([
                'originalProduct',
                'productRestrictions',
              ])?.filter(({ restriction }) =>
                ['LINE_GROUPS', 'ZONAL'].includes(restriction),
              );
              const category = getFieldValue(['originalProduct', 'category']);

              if (!restrictions || restrictions.length === 0) return <div />;

              return (
                <>
                  {restrictions.map((restrictionItem) => {
                    switch (restrictionItem.restriction) {
                      case 'LINE_GROUPS':
                        return (
                          <Restriction
                            form={configForm}
                            restrictions={restrictionItem}
                          />
                        );

                      case 'ZONAL':
                        return (
                          metropolitanEnabled &&
                          category === 'PASS' && (
                            <ZonalRestriction
                              form={configForm}
                              axisData={axis}
                              restrictions={restrictionItem}
                            />
                          )
                        );
                      default:
                        return <div />;
                    }
                  })}
                </>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Products;
